import {
  Box,
  Checkbox,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { SuggestedIcon } from "components/ArtificialIntelligence/SuggestedIcon";
import { safeParseFloat } from "encamp-shared/src/utils/safeParseNumber";
import { Control, Controller, FieldPath } from "react-hook-form";
import { FieldWarningIndicator } from "routes/Customer/Waste/Manifests/FieldWarningIndicator";

import { RevertHelperLine } from "../ReviewDocumentMetadataDialog/components/RevertHelperLine";
import { ExtractionReviewFormData, isFlagged, isReviewed } from "./formData";
import { ExtractionDraftValue, ExtractionFieldSpec, TRI_BOOLEAN_OPTIONS } from "./types";

function lowConfidenceTooltip(score: number | null): string {
  if (score == null) return "The extractor was not confident about this value — please verify it.";
  return `The extractor was ${Math.round(score * 100)}% confident about this value — please verify it.`;
}

/** How an extracted value reads back to the reviewer in the revert line. */
function displayExtracted(extracted: ExtractionDraftValue): string {
  if (extracted === true) return "Yes";
  if (extracted === false) return "No";
  if (extracted == null || extracted === "") return "(empty)";
  return extracted;
}

export interface ExtractionFieldControlProps {
  spec: ExtractionFieldSpec;
  control: Control<ExtractionReviewFormData>;
  path: FieldPath<ExtractionReviewFormData>;
  /** What the extractor proposed for this path, in draft form. */
  extracted: ExtractionDraftValue;
  /** False on a reviewer-added row, which the extractor never proposed. */
  hasCandidate: boolean;
  /** The extractor's confidence for this path, or null when it gave none. */
  confidence: number | null;
  lowConfidenceThreshold: number;
  /**
   * Visible label, inside the input as everywhere else in the app. A repeated
   * column keeps its plain label — the row it belongs to is named by the
   * surrounding group, so nothing has to repeat the row per field.
   */
  label: string;
  disabled: boolean;
}

/**
 * One extracted value, as an always-editable labelled control.
 *
 * Carries the two affordances the document-metadata review established: the
 * shared `SuggestedIcon` sparkle for a value the AI filled that nobody has
 * touched, and — layered on for this surface — a `FieldWarningIndicator` on the
 * end adornment (the slot `reviewDiffAdornment` uses for sources-disagree) when
 * the extractor's confidence was low.
 *
 * Both read `isReviewed`, a comparison against this value's own extracted
 * candidate, rather than `SuggestedField`/`useSuggestedValue`'s dirty check. In a
 * repeated group RHF's dirty state is positional, so removing a row would retire
 * every surviving row's flag; see `isReviewed` for the full reasoning.
 */
export function ExtractionFieldControl({
  spec,
  control,
  path,
  extracted,
  hasCandidate,
  confidence,
  lowConfidenceThreshold,
  label,
  disabled,
}: ExtractionFieldControlProps) {
  return (
    <Controller
      name={path}
      control={control}
      render={({ field, fieldState }) => {
        const value: ExtractionDraftValue =
          typeof field.value === "string" || typeof field.value === "boolean" ? field.value : null;
        const reviewed = hasCandidate ? isReviewed(spec, value, extracted) : true;
        const flagged = isFlagged(confidence, reviewed, lowConfidenceThreshold);
        // "The extractor produced something here" — the rule both the sparkle and
        // the revert line hang off, so they cannot disagree about which fields
        // have a reading behind them. Deliberately not `isBlankDraft`, which
        // counts boolean false as blank and would hide both from a checkbox the
        // extractor genuinely read as false.
        const hasReading =
          extracted !== null && !(typeof extracted === "string" && extracted.trim() === "");
        const suggested = hasCandidate && !reviewed && hasReading;

        const warning = flagged ? (
          <FieldWarningIndicator
            tooltip={lowConfidenceTooltip(confidence)}
            ariaLabel={`Low confidence: ${label}`}
            inline
          />
        ) : null;

        // Nothing to restore when the extractor read nothing: the field is
        // showing something the record already held, and "Suggested: (empty)"
        // beside a live Revert offers to erase it in one click, labelled as
        // putting back a reading that was never made.
        const revertLine =
          hasCandidate && reviewed && hasReading ? (
            <RevertHelperLine
              // "Suggested" is what the rest of the product calls a value the AI
              // proposed — the sparkle, `SuggestedField`, "Suggested title" in the
              // document review. "Extracted" named the pipeline instead.
              label="Suggested"
              displayValue={displayExtracted(extracted)}
              tooltip={`Restore what we read for ${label}`}
              onClick={() => field.onChange(extracted)}
            />
          ) : null;

        if (spec.kind === "boolean") {
          return (
            <Box>
              <Stack direction="row" alignItems="center">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={value === true}
                      onChange={(event) => field.onChange(event.target.checked)}
                      onBlur={field.onBlur}
                      disabled={disabled}
                      size="small"
                    />
                  }
                  label={label}
                />
                {suggested && <SuggestedIcon isSuggested size="small" />}
                {warning}
              </Stack>
              {revertLine}
            </Box>
          );
        }

        const textValue = typeof value === "string" ? value : "";
        const isTriBoolean = spec.kind === "triBoolean";
        const isSelect = spec.kind === "select" || isTriBoolean;
        // A tri-state's options are fixed; a select's come from the caller. The
        // empty option below is what carries "not answered".
        const options = isTriBoolean ? TRI_BOOLEAN_OPTIONS : (spec.options ?? []);
        // An extractor can emit a value outside the caller's option list. Keeping
        // it as an option means the reviewer sees what will be committed instead
        // of a blank field hiding a value they never agreed to.
        const unrecognizedOption =
          isSelect && textValue !== "" && !options.some((option) => option.value === textValue)
            ? textValue
            : null;
        // safeParseFloat nulls anything it can't read; say so rather than
        // discarding what the reviewer typed without comment.
        const unparseableNumber =
          spec.kind === "number" && textValue.trim() !== "" && safeParseFloat(textValue) == null;

        const helperText = (() => {
          if (fieldState.error?.message != null) return fieldState.error.message;
          if (unparseableNumber) return "Enter a number";
          if (unrecognizedOption != null) return "Not one of the expected values — please confirm";
          return spec.helperText;
        })();

        return (
          <Box>
            <TextField
              select={isSelect}
              multiline={spec.kind === "multiline"}
              minRows={spec.kind === "multiline" ? 2 : undefined}
              size="small"
              fullWidth
              label={label}
              disabled={disabled}
              value={textValue}
              name={field.name}
              inputRef={field.ref}
              // Normalizing "" back to null keeps a cleared field indistinguishable
              // from one the extractor never filled, which is what lets a
              // type-then-delete stop counting as a review. Matches FormTextField.
              onChange={(event) =>
                field.onChange(event.target.value === "" ? null : event.target.value)
              }
              onBlur={field.onBlur}
              error={!!fieldState.error || unparseableNumber}
              helperText={helperText}
              slotProps={{
                input: {
                  startAdornment: suggested ? (
                    <Box
                      sx={{
                        display: "flex",
                        // A multiline input grows downward and the row centres
                        // its children, so the sparkle drifts away from the
                        // value it marks. Pinned to the top instead, with the
                        // icon's own block margin dropped: the input's padding
                        // already places the adornment on the first line, and a
                        // 20px icon fills that line's 20.125px box, so any
                        // margin on top of it hangs the icon below the line.
                        ...(spec.kind === "multiline"
                          ? { alignSelf: "flex-start", "& .MuiSvgIcon-root": { my: 0 } }
                          : {}),
                      }}
                    >
                      <SuggestedIcon isSuggested size="small" />
                    </Box>
                  ) : undefined,
                  endAdornment:
                    spec.unit != null || warning != null ? (
                      <Stack direction="row" alignItems="center" spacing={0.5}>
                        {spec.unit != null && (
                          <Typography variant="body2" color="text.secondary">
                            {spec.unit}
                          </Typography>
                        )}
                        {warning}
                      </Stack>
                    ) : undefined,
                },
                htmlInput: {
                  inputMode: spec.kind === "number" ? "decimal" : undefined,
                },
              }}
            >
              {isSelect && <MenuItem value="">—</MenuItem>}
              {unrecognizedOption != null && (
                <MenuItem value={unrecognizedOption}>{unrecognizedOption}</MenuItem>
              )}
              {isSelect &&
                options.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
            </TextField>
            {revertLine}
          </Box>
        );
      }}
    />
  );
}
